// Analytics utility for tracking events
// Supports Google Analytics 4 and custom event tracking

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Update Google Consent Mode v2 signals. Call after the user accepts or
 * declines the cookie banner. Defaults (set in index.html) are "denied" so
 * no tag fires until this is called with granted values.
 */
export const updateConsent = (granted: boolean) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  const state = granted ? 'granted' : 'denied';
  window.gtag?.('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
    functionality_storage: state,
    personalization_storage: state,
  });
};

export type AnalyticsEvent = {
  action: string;
  category: string;
  label?: string;
  value?: number;
};

/**
 * Initialize Google Analytics
 * Call this once in your app initialization
 */
export const initAnalytics = (measurementId: string) => {
  if (typeof window === 'undefined') return;

  // Load gtag script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date().toISOString());
  window.gtag('config', measurementId, {
    page_path: window.location.pathname,
  });
};

/**
 * Track page views
 */
export const trackPageView = (url: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', 'GA_MEASUREMENT_ID', {
    page_path: url,
    page_title: title,
  });

  console.log('📊 Page View:', { url, title });
};

/**
 * Track custom events
 */
export const trackEvent = ({ action, category, label, value }: AnalyticsEvent) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.log('📊 Event (no GA):', { action, category, label, value });
    return;
  }

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });

  console.log('📊 Event:', { action, category, label, value });
};

/**
 * Pre-defined tracking functions for common actions
 */
export const analytics = {
  // CTA Interactions
  clickScheduleCall: (location: string) => {
    trackEvent({
      action: 'click_schedule_call',
      category: 'CTA',
      label: location,
    });
  },

  clickAuditCTA: (location: string) => {
    trackEvent({
      action: 'click_audit_cta',
      category: 'CTA',
      label: location,
    });
  },

  // Form Submissions
  submitContactForm: (email: string) => {
    trackEvent({
      action: 'submit_contact_form',
      category: 'Form',
      label: email,
    });
  },

  submitBookingForm: (name: string) => {
    trackEvent({
      action: 'submit_booking_form',
      category: 'Form',
      label: name,
    });
  },

  // Audit Flow
  startAudit: () => {
    trackEvent({
      action: 'start_audit',
      category: 'Audit',
      label: 'landing_page',
    });
  },

  completeAuditStep: (step: number) => {
    trackEvent({
      action: 'complete_audit_step',
      category: 'Audit',
      label: `step_${step}`,
      value: step,
    });
  },

  submitAudit: (score: number) => {
    trackEvent({
      action: 'submit_audit',
      category: 'Audit',
      label: 'complete',
      value: score,
    });
  },

  // Navigation
  clickNavLink: (linkLabel: string) => {
    trackEvent({
      action: 'click_nav_link',
      category: 'Navigation',
      label: linkLabel,
    });
  },

  // Engagement
  scrollToSection: (section: string) => {
    trackEvent({
      action: 'scroll_to_section',
      category: 'Engagement',
      label: section,
    });
  },

  openChatWidget: () => {
    trackEvent({
      action: 'open_chat_widget',
      category: 'Engagement',
      label: 'chat',
    });
  },

  // Social
  clickSocialLink: (platform: string) => {
    trackEvent({
      action: 'click_social_link',
      category: 'Social',
      label: platform,
    });
  },

  // Cookie Consent
  acceptCookies: () => {
    trackEvent({
      action: 'accept_cookies',
      category: 'Privacy',
      label: 'consent',
    });
  },

  declineCookies: () => {
    trackEvent({
      action: 'decline_cookies',
      category: 'Privacy',
      label: 'consent',
    });
  },
};
